import React from "react";
import { IoIosArrowRoundForward } from "react-icons/io";
import { Autoplay, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { FaShoppingCart, FaStar } from "react-icons/fa";
import { ProductSkeleton } from "../../components/common/ProductSkeleton";
import { useCart } from "../../hooks/CartContext";

const NewlyArrived = () => {
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["newly-arrived-products"],
    queryFn: async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/products/product/newly-arrived`
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || "Something went wrong");
      return json;
    },
  });

  const products = data?.products ?? [];

  return (
    <section className="bg-black w-full">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <h2 className="text-white font-bold text-2xl sm:text-3xl">
            Newly Arrived <span className="text-orange-600">Products</span>
          </h2>

          <Link
            to="/consoles"
            className="inline-flex items-center justify-center gap-2 text-white text-sm font-semibold
              rounded-xl px-4 py-2 border border-gray-600 hover:text-red hover:border-red transition"
          >
            <span>See All</span>
            <IoIosArrowRoundForward size={20} />
          </Link>
        </div>

        {/* Error */}
        {isError && (
          <div className="mt-6 rounded-2xl border border-gray-700 bg-black/30 p-5">
            <p className="text-sm text-red">{error?.message}</p>
          </div>
        )}

        {/* Slider */}
        <div className="mt-6">
          {/* CHANGED: Added !overflow-visible to prevent clipping when scaling */}
          <Swiper
            modules={[Navigation, Autoplay]}
            loop
            navigation
            autoplay={{ delay: 4000, disableOnInteraction: false }}
            breakpoints={{
              0: { slidesPerView: 1.15, spaceBetween: 12 },
              480: { slidesPerView: 2, spaceBetween: 14 },
              640: { slidesPerView: 2.4, spaceBetween: 16 },
              1024: { slidesPerView: 3, spaceBetween: 18 },
              1280: { slidesPerView: 5, spaceBetween: 22 },
            }}
            className="rounded-xl !overflow-visible"
            style={{ "--swiper-navigation-color": "#ffffff" }}
          >
            {/* Loading */}
            {isPending &&
              Array.from({ length: 8 }).map((_, i) => (
                <SwiperSlide key={i}>
                  <ProductSkeleton />
                </SwiperSlide>
              ))}

            {/* Data */}
            {!isPending &&
              !isError &&
              products.slice(0, 10).map((p) => {
                const soldOut = Number(p?.quantity || 0) < 1;

                return (
                  <SwiperSlide key={p._id}>
                    {/* CHANGED: Added hover:scale-105, hover:z-10, and shadow enhancements */}
                    <div
                      onClick={() => navigate(`/product/${p._id}`)}
                      className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-2xl hover:scale-105 hover:z-10 cursor-pointer transition-all duration-300 overflow-hidden relative"
                    >
                      <div className="overflow-hidden">
                        <img
                          src={p?.images?.[0] || "/placeholder.png"}
                          alt={p?.name || "Product"}
                          className="h-44 sm:h-48 w-full object-cover bg-gray-100"
                        />
                      </div>

                      <div className="p-4 relative bg-white">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            {/* CHANGED: Added transition-colors and group-hover:text-red */}
                            <h3 className="font-semibold text-gray-900 truncate transition-colors group-hover:text-red">
                              {p?.name}
                            </h3>
                            <p className="text-xs text-gray-500 truncate">
                              {p?.category}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between mt-2 text-sm">
                          <span className="flex items-center gap-1 text-yellow-500 font-bold">
                            <FaStar /> {p?.averageRating || 0}
                          </span>
                        </div>

                        <div className="flex items-center justify-between mt-3">
                          <span className="font-bold text-gray-900">
                            ₦{Number(p?.finalPrice || 0).toLocaleString()}
                          </span>
                          {p?.discount > 0 && (
                            <>
                              {!!p?.price && (
                                <span className="line-through text-red text-sm">
                                  ₦{Number(p?.price || 0).toLocaleString()}
                                </span>
                              )}
                            </>
                          )}
                        </div>

                        {/* CHANGED: Enhanced button states with active:scale-95 and dynamic shadows */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (soldOut) return;
                            addToCart(p);
                          }}
                          disabled={soldOut}
                          className={`mt-4 w-full py-2.5 rounded-xl flex items-center justify-center gap-2 font-semibold transition-all duration-300 active:scale-95
                            ${
                              soldOut
                                ? "bg-gray-200 text-gray-600 cursor-not-allowed"
                                : "bg-red text-white hover:bg-red/90 shadow-md hover:shadow-lg"
                            }`}
                        >
                          <FaShoppingCart />
                          {soldOut ? "Sold Out" : "Add to Cart"}
                        </button>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
          </Swiper>
        </div>
      </div>
    </section>
  );
};

export default NewlyArrived;